<template>
  <div class="document-container" style="padding-top: 0; padding-bottom: 0;">
    <ul class="summary-panels wrap is-flex">
      <li class="document flex-item flex-item-header grow">
        <div
          class="panel-container panel-container--search"
          :class="{ 'flex-align--start': !loadingError }">
          <div v-if="loadingError" class="is-size-7">
            <es-exception
              exceptionMessage="Error Loading Location Groups"
              margin="0rem" style="margin: auto"/>
          </div>
          <template v-else>
            <es-select
              v-if="locationGroupLength > 1"
              label="Group:"
              v-bind:search="true"
              api="cep/location-groups"
              :apiQueryParams="locationGroupQueryParams"
              v-on:update:values="selectedGroup = $event"
              ref="selGroups"
              :valueTplFn="(result) => `${result.label}`"
              :preSelected="locationGroupId ? [locationGroupId] : null"
              searchMinLength="3"
              keyValue="id"/>
            <span
              v-else
              class="has-font-weight-bold is-size-4"
              :title="selectedLocationGroup.label">
              {{ selectedLocationGroup.label ? selectedLocationGroup.label : '' }}
            </span>
          </template>
        </div>
      </li>
      <li class="document flex-item">
        <div class="panel-container is-flex">
          <es-loader v-if="loadingSummary" size="2x" style="margin-bottom: 0.25rem"/>
          <div v-else class="is-size-4 has-text-weight-bold">
            {{ cepSummary.identifiedCount || '--' }}
          </div>
          <div class="panel-subtitle has-text-grey">
            Total Identified <br/> (Current Year)
          </div>
        </div>
      </li>
      <li class="document flex-item">
        <div class="panel-container is-flex">
          <es-loader v-if="loadingSummary" size="2x" style="margin-bottom: 0.25rem"/>
          <div v-else class="is-size-4 has-text-weight-bold">
            {{ cepSummary.enrolledCount || '--' }}
          </div>
          <div class="panel-subtitle has-text-grey">
            Total Enrolled <br/> (Current Year)
          </div>
        </div>
      </li>
      <li class="document flex-item is-flex">
        <div class="panel-container--isp is-flex">
          <div class="isp-box is-flex flex-justify--center">
            <es-loader v-if="loadingSummary" size="2x" style="margin: 0 auto 0.25rem auto"/>
            <div
              v-else-if="!loadingSummary && !cepSummary.totalISP"
              class="is-size-4 has-text-weight-bold">
              --
            </div>
            <div v-else class="is-flex flex-align--center">
              <div class="is-size-4 has-text-weight-bold is-flex">
                {{ (cepSummary.totalISP * 100).toFixed(2) }}
              </div>
              <div class="icon-isp">
                <es-trend-indicator
                  :prevISP="cepSummary.prevISP"
                  :currentISP="cepSummary.totalISP"/>
              </div>
            </div>
            <div class="panel-subtitle has-text-grey">
              ISP %<br/>(Current Year)
            </div>
          </div>
        </div>
      </li>
      <li class="document flex-item">
        <div class="panel-container is-flex">
          <es-loader v-if="loadingSummary" size="2x" style="margin-bottom: 0.25rem"/>
          <div
            v-else-if="!loadingSummary && !cepSummary.populationStatus"
            class="is-size-4 has-text-weight-bold">
            --
          </div>
          <div v-else
            class="is-size-4 has-text-weight-bold"
            :class="{'has-text-danger' : cepSummary.populationStatus === 'D'}">
            {{ cepSummary.populationStatus === 'D'
                ? populationDueDate : cepStatuses[cepSummary.populationStatus] }}
          </div>
          <div class="panel-subtitle has-text-grey">
            Population Status
          </div>
          <div class="panel-subtitle has-text-grey">
            <template v-if="cepSummary.populationStatus === 'S'">
              Certified:
              {{
                formatDateToDisplay(
                  approvalCertifiedInfo.populationCertifiedDate
                )
              }}
            </template>
            <template v-else-if="cepSummary.populationStatus === 'A'">
              Accepted:
              {{
                formatDateToDisplay(
                  approvalCertifiedInfo.populationApprovalDate
                )
              }}
            </template>
            <template v-else>
              Certified: N/A
            </template>
          </div>
        </div>
      </li>
      <li class="document flex-item">
        <div class="panel-container is-flex">
          <es-loader v-if="loadingSummary" size="2x" style="margin-bottom: 0.25rem"/>
          <div
            v-else-if="!loadingSummary && !cepSummary.electionStatus"
            class="is-size-4 has-text-weight-bold">
            --
          </div>
          <div v-else
            class="is-size-4 has-text-weight-bold"
            :class="{'has-text-danger' : cepSummary.electionStatus === 'D'}">
            {{ cepSummary.electionStatus === 'D'
                ? electionsDueDate : cepStatuses[cepSummary.electionStatus] }}
          </div>
          <div class="panel-subtitle has-text-grey">
            Election Status
          </div>
          <div class="panel-subtitle has-text-grey">
            <template v-if="cepSummary.electionStatus === 'S'">
              Certified:
              {{
                formatDateToDisplay(
                  approvalCertifiedInfo.electionsCertifiedDate
                )
              }}
            </template>
            <template v-else-if="cepSummary.electionStatus === 'A'">
              Accepted:
              {{
                formatDateToDisplay(
                  approvalCertifiedInfo.electionsApprovalDate
                )
              }}
            </template>
            <template v-else>
              Certified: N/A
            </template>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import moment from 'moment';

import {
  mapActions,
  mapGetters,
} from 'vuex';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import EsTrendIndicator from '@/components/EsTrendIndicator.vue';
import FieldLabels from '@/mixins/FieldLabels';


export default {
  mixins: [
    FieldLabels,
  ],
  components: {
    EsException,
    EsLoader,
    EsSelect,
    EsTrendIndicator,
  },
  props: {
    approvalCertifiedInfo: {
      type: Object,
    },
    cepSummary: {
      type: Object,
    },
    cepTab: {
      type: String,
    },
    loading: {
      type: Boolean,
    },
    loadingError: {
      type: Boolean,
    },
    loadingSummary: {
      type: Boolean,
    },
    locationGroupId: {
      type: [String, Number],
    },
    locationGroupLength: {
      type: Number,
    },
    selectedLocationGroup: {
      type: Object,
    },
    schoolYear: {
      type: [String, Number],
    },
  },
  data() {
    return {
      cepStatuses: {
        S: 'Submitted',
        O: 'Reopen',
        A: 'Accepted',
        D: 'Due',
        P: 'Pending',
        R: 'Rejected',
      },
      selectedGroup: [],
    };
  },
  watch: {
    selectedGroup() {
      this.updateSelectedLocation();
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'defaultPersonType',
    ]),
    electionsDueDate() {
      return `${this.cepStatuses[this.cepSummary.electionStatus]}
        ${moment(this.cepSummary.electionsDueDate).format('MM/DD')}`;
    },
    locationGroupQueryParams() {
      return {
        organizations: 'FNS',
        status: true,
      };
    },
    populationDueDate() {
      return `${this.cepStatuses[this.cepSummary.populationStatus]}
        ${moment(this.cepSummary.populationDueDate).format('MM/DD')}`;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    formatDateToDisplay(date) {
      if (!date) return 'N/A';
      const m = moment(date);
      return m.isValid() ? m.format('MM/DD/YYYY hh:mm A') : 'N/A';
    },
    updateSelectedLocation() {
      if (Object.keys(this.selectedGroup).length) {
        const group = this.selectedGroup[0];
        this.$router.replace({
          path: `/directMatch/cep/${this.cepTab}`,
          query: {
            locationGroupId: group.id,
            schoolYear: this.$store.state.propertiesModule.properties['CEP-CEP_CURRENT_SCHOOL_YEAR'],
          },
        });

        this.$emit('selectedGroup', group);
      } else {
        this.$router.replace({
          path: `/directMatch/cep/${this.cepTab}`,
          query: {
            schoolYear: this.$store.state.propertiesModule.properties['CEP-CEP_CURRENT_SCHOOL_YEAR'],
          },
        });
        this.$emit('selectedGroup', {});
      }
    },
  },
};
</script>

<style lang="sass" scoped>

  ::v-deep .panel-container--search
    .field
      .select
        width: 100%
        select
          width: 100%

  .wrap
    -webkit-flex-wrap: wrap
    flex-wrap: wrap
  .flex-item
    min-width: 200px
    min-height: 80px
    margin: 0rem 1rem 1rem 0rem
    text-align: center
  .flex-item-header
    min-width: 100px
  .summary-panels
    margin-right: -1rem
    justify-content: space-between
    flex-direction: row
  .panel-container
    padding: 1rem
    height: 100%
    flex-direction: column
    justify-content: flex-start
    align-items: center
  .panel-container--isp
    padding: 1rem
    height: 100%
    width: 100%
    justify-content: center
  .isp-box
    flex-direction: column
  .panel-subtitle
    font-size: 12px
  .icon-isp
    padding-left: .5rem
  .popper
    background-color: #fff !important
    -webkit-box-shadow: available !important
    box-shadow: available !important
    width: 100% !important
    padding: 0 !important
    text-align: left
    .search-result
      font-size: .85rem
      display: flex
      align-items: center
      min-height: 30px
      padding-left: .5rem !important
      padding-right: .5rem !important
      width: 100%
      white-space: nowrap
      overflow: hidden
      text-overflow: ellipsis
      &:hover
        background-color: #1E90FF
        color: #fff
    .popper__arrow
      top: 0px
    a
      padding: 4px 24px
      height: 25px
      &:hover
        color: #5953A4 !important

</style>
